import React from 'react'
import { Link } from 'react-router-dom'
import { CONTACT_EMAIL } from '../../config'

interface Props {
  section: 'privacy' | 'terms'
}

const PRIVACY_SECTIONS = [
  { id: 'p1', title: 'What we collect',         icon: '🗂️' },
  { id: 'p2', title: 'How we use your data',    icon: '⚙️' },
  { id: 'p3', title: 'Data storage & security', icon: '🔒' },
  { id: 'p4', title: 'Third-party services',    icon: '🔗' },
  { id: 'p5', title: 'Your rights',             icon: '✋' },
  { id: 'p6', title: 'Cookies',                 icon: '🍪' },
  { id: 'p7', title: 'Policy changes',          icon: '📢' },
  { id: 'p8', title: 'Contact',                 icon: '✉️' },
]

const TERMS_SECTIONS = [
  { id: 't1',  title: 'Acceptance',                        icon: '✅' },
  { id: 't2',  title: 'The service',                       icon: '🚀' },
  { id: 't3',  title: 'Your responsibilities',             icon: '📋' },
  { id: 't4',  title: 'Subscriptions & billing',           icon: '💳' },
  { id: 't5',  title: 'Free tier & limits',                icon: '🎁' },
  { id: 't6',  title: 'Intellectual property',             icon: '©️'  },
  { id: 't7',  title: 'Disclaimers & liability',           icon: '⚖️' },
  { id: 't8',  title: 'Termination',                       icon: '🚪' },
  { id: 't9',  title: 'Governing law',                     icon: '🏛️' },
  { id: 't10', title: 'Changes to these terms',            icon: '📢' },
  { id: 't11', title: 'Contact',                           icon: '✉️' },
]

export function LegalPage({ section }: Props) {
  const [activeId, setActiveId] = React.useState<string>('')

  React.useEffect(() => {
    const stored = localStorage.getItem('nudge-theme')
    if (stored === 'light' || stored === 'dark') {
      document.documentElement.setAttribute('data-theme', stored)
    }
  }, [])

  React.useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [section])

  React.useEffect(() => {
    const ids = section === 'privacy'
      ? PRIVACY_SECTIONS.map(s => s.id)
      : TERMS_SECTIONS.map(s => s.id)

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries.filter(e => e.isIntersecting)
        if (visible.length > 0) setActiveId(visible[0].target.id)
      },
      { rootMargin: '-20% 0px -70% 0px' }
    )

    ids.forEach(id => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [section])

  const toc = section === 'privacy' ? PRIVACY_SECTIONS : TERMS_SECTIONS

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>

      {/* Header */}
      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 40,
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg)',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 56,
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <img src="/logo.svg" alt="Nudge" style={{ width: 26, height: 26 }} />
          <span style={{ fontFamily: 'Fraunces, serif', fontSize: 17, fontWeight: 600, color: 'var(--text)' }}>
            Nudge
          </span>
        </Link>
        <Link to="/" style={{
          fontSize: 13,
          color: 'var(--text2)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}>
          ← Back to home
        </Link>
      </header>

      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '0 24px', display: 'flex', gap: 64, alignItems: 'flex-start' }}>

        {/* Sidebar TOC */}
        <aside style={{
          width: 220,
          flexShrink: 0,
          position: 'sticky',
          top: 72,
          paddingTop: 48,
          paddingBottom: 48,
          display: 'flex',
          flexDirection: 'column',
        }} className="hidden lg:flex">

          {/* Doc switcher */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 28 }}>
            <Link to="/privacy" style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 10px',
              borderRadius: 8,
              textDecoration: 'none',
              fontSize: 13,
              fontWeight: 600,
              background: section === 'privacy' ? 'var(--accent-bg)' : 'transparent',
              color: section === 'privacy' ? 'var(--accent)' : 'var(--text2)',
              border: section === 'privacy' ? '1px solid var(--accent-bd)' : '1px solid transparent',
            }}>
              <span style={{ fontSize: 15 }}>🔐</span> Privacy Policy
            </Link>
            <Link to="/terms" style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 10px',
              borderRadius: 8,
              textDecoration: 'none',
              fontSize: 13,
              fontWeight: 600,
              background: section === 'terms' ? 'var(--accent-bg)' : 'transparent',
              color: section === 'terms' ? 'var(--accent)' : 'var(--text2)',
              border: section === 'terms' ? '1px solid var(--accent-bd)' : '1px solid transparent',
            }}>
              <span style={{ fontSize: 15 }}>📄</span> Terms of Service
            </Link>
          </div>

          <div style={{ height: 1, background: 'var(--border)', marginBottom: 20 }} />

          {/* Section links */}
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {toc.map(s => (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={e => {
                  e.preventDefault()
                  document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  padding: '5px 10px',
                  borderRadius: 6,
                  textDecoration: 'none',
                  fontSize: 12.5,
                  fontWeight: activeId === s.id ? 600 : 400,
                  color: activeId === s.id ? 'var(--text)' : 'var(--text3)',
                  background: activeId === s.id ? 'var(--surface)' : 'transparent',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{ fontSize: 13, opacity: 0.8 }}>{s.icon}</span>
                {s.title}
              </a>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main style={{ flex: 1, minWidth: 0, paddingTop: 48, paddingBottom: 80 }}>

          {/* Mobile tab switcher */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 32 }} className="flex lg:hidden">
            <Link to="/privacy" style={{
              padding: '8px 18px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              textDecoration: 'none',
              background: section === 'privacy' ? 'var(--accent)' : 'var(--surface)',
              color: section === 'privacy' ? '#fff' : 'var(--text2)',
              border: '1px solid var(--border)',
            }}>
              🔐 Privacy
            </Link>
            <Link to="/terms" style={{
              padding: '8px 18px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              textDecoration: 'none',
              background: section === 'terms' ? 'var(--accent)' : 'var(--surface)',
              color: section === 'terms' ? '#fff' : 'var(--text2)',
              border: '1px solid var(--border)',
            }}>
              📄 Terms
            </Link>
          </div>

          {section === 'privacy' ? (
            <PrivacyContent />
          ) : (
            <TermsContent />
          )}
        </main>
      </div>
    </div>
  )
}

/* ── Shared building blocks ─────────────────────────────── */

function PageHeader({ icon, title, updated }: { icon: string; title: string; updated: string }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <span style={{ fontSize: 32 }}>{icon}</span>
        <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: 30, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
          {title}
        </h1>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text3)', margin: 0 }}>Last updated: {updated}</p>
    </div>
  )
}

function BetaBanner({ text }: { text: string }) {
  return (
    <div style={{
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
      background: 'var(--accent-bg)',
      border: '1px solid var(--accent-bd)',
      borderRadius: 10,
      padding: '12px 16px',
      marginBottom: 40,
    }}>
      <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>🧪</span>
      <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: 'var(--text2)' }}>
        <strong style={{ color: 'var(--text)' }}>Beta product:</strong> {text}
      </p>
    </div>
  )
}

function Section({ id, icon, num, title, summary, children }: {
  id: string
  icon: string
  num: string
  title: string
  summary: string
  children: React.ReactNode
}) {
  return (
    <section id={id} style={{ marginBottom: 40, scrollMarginTop: 80 }}>
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        overflow: 'hidden',
      }}>
        {/* Section header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '16px 20px',
          borderBottom: '1px solid var(--border)',
        }}>
          <span style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--text3)',
            fontFamily: 'monospace',
            minWidth: 24,
          }}>{num}</span>
          <span style={{ fontSize: 18 }}>{icon}</span>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{title}</h2>
        </div>

        {/* Plain-language summary */}
        <div style={{
          padding: '10px 20px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', flexShrink: 0, marginTop: 2, letterSpacing: '0.05em' }}>
            TL;DR
          </span>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text2)', fontStyle: 'italic', lineHeight: 1.55 }}>
            {summary}
          </p>
        </div>

        {/* Full text */}
        <div style={{
          padding: '16px 20px',
          fontSize: 14,
          lineHeight: 1.8,
          color: 'var(--text2)',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}>
          {children}
        </div>
      </div>
    </section>
  )
}

function ContactLine() {
  return (
    <p style={{ margin: 0 }}>
      Email us at{' '}
      <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>
        {CONTACT_EMAIL}
      </a>
      {' '}and we'll respond within 2 business days.
    </p>
  )
}

/* ── Privacy Policy ─────────────────────────────────────── */

function PrivacyContent() {
  return (
    <>
      <PageHeader icon="🔐" title="Privacy Policy" updated="May 2025" />
      <BetaBanner text="Nudge is in beta. Data practices and this policy may evolve. We'll notify you of any material changes before they take effect." />

      <Section id="p1" num="01" icon="🗂️" title="What we collect"
        summary="We collect what you give us and basic usage data — nothing more.">
        <p>We collect information you provide directly: your name, email address, and business details when you create an account. We also collect the contact data you upload (names, emails, visit dates) to power the nudge service.</p>
        <p>We automatically collect usage data such as pages visited, feature interactions, and email delivery events (opens, clicks, bounces) via Resend webhooks.</p>
      </Section>

      <Section id="p2" num="02" icon="⚙️" title="How we use your data"
        summary="Your data runs the service — we never sell it.">
        <p>We use your data solely to operate and improve Nudge:</p>
        <ul style={{ margin: '4px 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <li>To generate and send re-engagement emails to your contacts on your behalf.</li>
          <li>To display analytics and nudge history in your dashboard.</li>
          <li>To send you transactional emails (account verification, billing receipts).</li>
          <li>To improve our AI models in aggregate, anonymised form — never linked back to individual contacts.</li>
        </ul>
        <p style={{ fontWeight: 600, color: 'var(--text)' }}>We do not sell your data or your contacts' data to third parties.</p>
      </Section>

      <Section id="p3" num="03" icon="🔒" title="Data storage & security"
        summary="US-based servers, encrypted in transit and at rest. No card details stored by us.">
        <p>Data is stored on servers in the United States. We use industry-standard encryption (TLS in transit, AES-256 at rest) and restrict internal access on a need-to-know basis. We do not store payment card details — payments are processed by Stripe.</p>
      </Section>

      <Section id="p4" num="04" icon="🔗" title="Third-party services"
        summary="We use a small set of trusted vendors to deliver the service.">
        <p>We use the following sub-processors:</p>
        <ul style={{ margin: '4px 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <li><strong style={{ color: 'var(--text)' }}>Anthropic / OpenRouter</strong> — AI email generation. Contact data is passed as prompt context and is not retained by Anthropic per their API policy.</li>
          <li><strong style={{ color: 'var(--text)' }}>Resend</strong> — Email delivery and event webhooks.</li>
          <li><strong style={{ color: 'var(--text)' }}>Stripe</strong> — Subscription billing.</li>
          <li><strong style={{ color: 'var(--text)' }}>Render / Vercel</strong> — Infrastructure hosting.</li>
        </ul>
      </Section>

      <Section id="p5" num="05" icon="✋" title="Your rights"
        summary="You can access, correct, or delete your data at any time.">
        <p>You may request access to, correction of, or deletion of your account data and your contacts' data at any time by emailing us. We will respond within 30 days.</p>
        <p>You may export your contacts CSV from the dashboard at any time. Closing your account permanently deletes all associated data within 30 days.</p>
        <ContactLine />
      </Section>

      <Section id="p6" num="06" icon="🍪" title="Cookies"
        summary="Session and analytics cookies only — no ad tracking.">
        <p>We use cookies only for authentication (session tokens) and analytics. We do not use advertising or tracking cookies.</p>
      </Section>

      <Section id="p7" num="07" icon="📢" title="Policy changes"
        summary="14 days' notice before any material change.">
        <p>We will notify you by email at least 14 days before any material change to this policy. Continued use of the service after that date constitutes acceptance.</p>
      </Section>

      <Section id="p8" num="08" icon="✉️" title="Contact"
        summary="Questions? We're happy to help.">
        <ContactLine />
      </Section>
    </>
  )
}

/* ── Terms of Service ───────────────────────────────────── */

function TermsContent() {
  return (
    <>
      <PageHeader icon="📄" title="Terms of Service" updated="May 2025" />
      <BetaBanner text="Nudge is in beta. The service is provided on an early-access basis and may be modified, limited, or discontinued at any time with notice." />

      <Section id="t1" num="01" icon="✅" title="Acceptance"
        summary="Using Nudge means you agree to these Terms.">
        <p>By creating an account or using the Nudge service ("Service") you agree to these Terms. If you are using the Service on behalf of a business, you represent that you have authority to bind that business.</p>
      </Section>

      <Section id="t2" num="02" icon="🚀" title="The service"
        summary="Nudge sends AI re-engagement emails on your behalf — you're responsible for your contacts.">
        <p>Nudge helps small businesses send AI-generated re-engagement emails to lapsed customers. You remain fully responsible for the content sent to your contacts and for maintaining a valid opt-in relationship with them.</p>
      </Section>

      <Section id="t3" num="03" icon="📋" title="Your responsibilities"
        summary="Only upload contacts with a genuine relationship with your business. Follow applicable email laws.">
        <ul style={{ margin: '4px 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <li>Only upload contacts who have a genuine prior relationship with your business and have not opted out of marketing communications.</li>
          <li>Comply with all applicable email marketing laws (CAN-SPAM, CASL, GDPR where applicable).</li>
          <li>Do not use the Service to send spam, harass individuals, or send content that is illegal, deceptive, or harmful.</li>
          <li>Keep your account credentials secure.</li>
        </ul>
      </Section>

      <Section id="t4" num="04" icon="💳" title="Subscriptions & billing"
        summary="Monthly or annual billing via Stripe. 30 days' notice before any price change.">
        <p>Paid plans are billed monthly or annually via Stripe. All fees are non-refundable except where required by law or at our sole discretion. We reserve the right to change pricing with 30 days' notice. Downgrading takes effect at the next billing cycle.</p>
      </Section>

      <Section id="t5" num="05" icon="🎁" title="Free tier & limits"
        summary="Free accounts have contact and send limits. We may adjust these with reasonable notice.">
        <p>Free accounts are subject to contact and send limits as described on the pricing page. We reserve the right to modify free-tier limits at any time with reasonable notice.</p>
      </Section>

      <Section id="t6" num="06" icon="©️" title="Intellectual property"
        summary="The platform is ours. Your data is yours.">
        <p>The Nudge platform, logo, and all software are our property. You retain all rights to the business data and contact lists you upload. By using the Service you grant us a limited licence to process that data solely to provide the Service.</p>
      </Section>

      <Section id="t7" num="07" icon="⚖️" title="Disclaimers & liability"
        summary="Provided as-is. Our liability is capped at fees paid in the prior 3 months.">
        <p>The Service is provided "as is". We make no guarantees about email deliverability, customer recovery rates, or AI output quality. To the maximum extent permitted by law, our total liability to you for any claim is limited to the fees you paid in the three months preceding the claim.</p>
      </Section>

      <Section id="t8" num="08" icon="🚪" title="Termination"
        summary="Either side can end the relationship. Data deleted within 30 days.">
        <p>Either party may terminate at any time. We may suspend or terminate your account immediately for violations of these Terms. Upon termination, your data will be deleted within 30 days per our Privacy Policy.</p>
      </Section>

      <Section id="t9" num="09" icon="🏛️" title="Governing law"
        summary="Disputes resolved through arbitration or small claims court.">
        <p>These Terms are governed by the laws of the jurisdiction in which Nudge operates, without regard to conflict-of-law principles. Any disputes shall be resolved through binding arbitration or small claims court.</p>
      </Section>

      <Section id="t10" num="10" icon="📢" title="Changes to these terms"
        summary="14 days' notice before any material change.">
        <p>We may update these Terms at any time. We will notify you by email at least 14 days before material changes take effect. Continued use after that date constitutes acceptance.</p>
      </Section>

      <Section id="t11" num="11" icon="✉️" title="Contact"
        summary="Questions? We're happy to help.">
        <ContactLine />
      </Section>
    </>
  )
}
